import {
  type CSSProperties,
  type MouseEvent,
  type RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  TASK_TRACKER_DRAWER_DEFAULT_WIDTH,
  TASK_TRACKER_DRAWER_MIN_WIDTH,
  TASK_TRACKER_DRAWER_WIDTH_STORAGE_KEY,
} from '../taskTrackerDrawer.constants'

type WorkspaceBounds = {
  top: number
  height: number
}

const readStoredDrawerWidth = () => {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(TASK_TRACKER_DRAWER_WIDTH_STORAGE_KEY)
    if (!raw) return null

    const parsed = Number(raw)
    if (!Number.isFinite(parsed) || parsed <= 0) return null

    return Math.round(parsed)
  } catch {
    return null
  }
}

const writeStoredDrawerWidth = (width: number) => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(
      TASK_TRACKER_DRAWER_WIDTH_STORAGE_KEY,
      String(Math.round(width)),
    )
  } catch {
    // ignore quota / privacy mode errors
  }
}

const getWorkspaceBounds = (
  workspaceRef: RefObject<HTMLElement | null>,
): WorkspaceBounds | null => {
  const workspace = workspaceRef.current
  if (!workspace) return null

  const rect = workspace.getBoundingClientRect()
  return {
    top: rect.top,
    height: rect.height,
  }
}

export const useTaskTrackerDrawerResize = (
  workspaceRef: RefObject<HTMLElement | null>,
  open: boolean,
) => {
  const [width, setWidth] = useState(() => {
    return readStoredDrawerWidth() ?? TASK_TRACKER_DRAWER_DEFAULT_WIDTH
  })
  const [isResizing, setIsResizing] = useState(false)
  const [workspaceBounds, setWorkspaceBounds] = useState<WorkspaceBounds | null>(
    null,
  )

  const getWidthLimits = useCallback(() => {
    const workspaceWidth = workspaceRef.current?.getBoundingClientRect().width
    if (!workspaceWidth) {
      return {
        minWidth: TASK_TRACKER_DRAWER_MIN_WIDTH,
        maxWidth: TASK_TRACKER_DRAWER_DEFAULT_WIDTH,
      }
    }

    const maxWidth = Math.max(1, Math.floor(workspaceWidth / 1.75))
    const minWidth = Math.min(TASK_TRACKER_DRAWER_MIN_WIDTH, maxWidth)

    return { minWidth, maxWidth }
  }, [workspaceRef])

  const clampWidth = useCallback(
    (nextWidth: number) => {
      const { minWidth, maxWidth } = getWidthLimits()
      return Math.min(maxWidth, Math.max(minWidth, nextWidth))
    },
    [getWidthLimits],
  )

  const syncWorkspaceBounds = useCallback(() => {
    setWorkspaceBounds(getWorkspaceBounds(workspaceRef))
  }, [workspaceRef])

  const handleResizeStart = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()

      const startX = event.clientX
      const startWidth = width
      let latestWidth = startWidth

      setIsResizing(true)

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        const delta = startX - moveEvent.clientX
        latestWidth = clampWidth(startWidth + delta)
        setWidth(latestWidth)
      }

      const handleMouseUp = () => {
        setIsResizing(false)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        writeStoredDrawerWidth(latestWidth)
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    },
    [clampWidth, width],
  )

  useEffect(() => {
    const workspace = workspaceRef.current
    if (!workspace) return

    const syncWidth = () => {
      setWidth((prev) => clampWidth(prev))
      syncWorkspaceBounds()
    }

    syncWidth()

    const observer = new ResizeObserver(syncWidth)
    observer.observe(workspace)

    return () => {
      observer.disconnect()
    }
  }, [clampWidth, syncWorkspaceBounds, workspaceRef])

  useEffect(() => {
    if (!open) return

    syncWorkspaceBounds()

    const handleWindowChange = () => {
      syncWorkspaceBounds()
      setWidth((prev) => clampWidth(prev))
    }

    window.addEventListener('resize', handleWindowChange)
    window.addEventListener('scroll', handleWindowChange, true)

    return () => {
      window.removeEventListener('resize', handleWindowChange)
      window.removeEventListener('scroll', handleWindowChange, true)
    }
  }, [clampWidth, open, syncWorkspaceBounds])

  const resizeHandleStyle: CSSProperties | undefined = workspaceBounds
    ? {
        top: workspaceBounds.top,
        height: workspaceBounds.height,
        right: width,
      }
    : undefined

  return {
    width,
    isResizing,
    resizeHandleStyle,
    handleResizeStart,
  }
}
